import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../components/ui/Button.jsx';
import { Card } from '../components/ui/Card.jsx';
import { useTranslation } from '../i18n/useTranslation.js';
import { usersApi } from '../services/usersApi.js';

/**
 * Das eigene Kennwort wechseln.
 *
 * Bis Paket 1 gab es das nicht - weder hier noch sonstwo. Die Anmeldeseite
 * verwies auf den Administrator, und der konnte es auch nicht. Wer sein
 * Kennwort ändern wollte, brauchte den Anbieter.
 */

/** Dieselbe Regel wie im Dienst. Steht sie nur dort, erfährt der Benutzer sie erst nach dem Absenden. */
const REGELN = [
  { key: 'settings.password.ruleLength', pruefe: (p) => p.length >= 10 },
  { key: 'settings.password.ruleLower', pruefe: (p) => /[a-z]/.test(p) },
  { key: 'settings.password.ruleUpper', pruefe: (p) => /[A-Z]/.test(p) },
  { key: 'settings.password.ruleDigit', pruefe: (p) => /\d/.test(p) },
];

const LEER = { currentPassword: '', newPassword: '', repeat: '' };

export function PasswordChangeCard() {
  const { t } = useTranslation();
  const [form, setForm] = useState(LEER);
  const [fehler, setFehler] = useState(null);
  const [fertig, setFertig] = useState(false);

  const setzen = (feld) => (e) => {
    setForm((p) => ({ ...p, [feld]: e.target.value }));
    setFehler(null);
    setFertig(false);
  };

  const erfuellt = REGELN.map((r) => ({ ...r, ok: r.pruefe(form.newPassword) }));
  const alleErfuellt = erfuellt.every((r) => r.ok);
  const gleich = form.newPassword.length > 0 && form.newPassword === form.repeat;
  const gueltig = form.currentPassword.length > 0 && alleErfuellt && gleich;

  const wechseln = useMutation({
    mutationFn: () =>
      usersApi.changeOwnPassword({
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      }),
    onSuccess: () => {
      setForm(LEER);
      setFehler(null);
      setFertig(true);
    },
    /* Die Meldung des Dienstes durchreichen. Er unterscheidet zwischen
       "bisheriges Kennwort falsch" und "neues gleicht dem alten"; eine eigene
       Ersatzmeldung nähme dem Benutzer genau diese Auskunft. */
    onError: (err) => {
      setFertig(false);
      setFehler(err?.message || t('common.loadError'));
    },
  });

  return (
    <Card title={t('settings.password.title')}>
      <p className="muted text-sm" style={{ marginTop: 0 }}>{t('settings.password.hint')}</p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (gueltig) wechseln.mutate();
        }}
      >
        <label className="field">
          <span className="field-label">{t('settings.password.current')}</span>
          <input
            className="input"
            type="password"
            autoComplete="current-password"
            value={form.currentPassword}
            onChange={setzen('currentPassword')}
          />
        </label>

        <label className="field">
          <span className="field-label">{t('settings.password.new')}</span>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            value={form.newPassword}
            onChange={setzen('newPassword')}
          />
        </label>

        {/* Die Regeln stehen DA, bevor abgesendet wird. Sonst erfährt man sie
            erst aus einer Fehlermeldung, und zwar eine nach der anderen. */}
        {form.newPassword.length > 0 && (
          <ul className="password-rules">
            {erfuellt.map((r) => (
              <li key={r.key} data-ok={r.ok || undefined}>
                <span aria-hidden="true">{r.ok ? '✓' : '·'}</span> {t(r.key)}
              </li>
            ))}
          </ul>
        )}

        <label className="field">
          <span className="field-label">{t('settings.password.repeat')}</span>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            value={form.repeat}
            onChange={setzen('repeat')}
          />
        </label>
        {form.repeat.length > 0 && !gleich && (
          <p className="text-sm" style={{ color: 'var(--color-danger)' }}>
            {t('settings.password.mismatch')}
          </p>
        )}

        {fehler && <div className="alert danger">{fehler}</div>}
        {fertig && <div className="alert success">{t('settings.password.done')}</div>}

        <div style={{ marginTop: 'var(--space-3)' }}>
          <Button variant="primary" type="submit" disabled={!gueltig || wechseln.isPending}>
            {wechseln.isPending ? t('common.saving') : t('settings.password.submit')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
